import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useCreateCreditMemoSendRequests } from "src/hooks/credit-memo-send/useCreateCreditMemoSendRequest";
import { useCreditMemoLookup } from "src/hooks/credit-memo-send/useCreditMemoLookup";
import throttle from "lodash/throttle";

const CreditMemoSendForm: React.FC = () => {
  const [tranIdsInput, setTranIdsInput] = useState<string>("");
  const [jiraTicketInput, setJiraTicketInput] = useState<string>("");
  const [tranIds, setTranIds] = useState<string[]>([]);
  const [rowData, setRowData] = useState<any[]>([]);
  const [isCreateRequestComplete, setIsCreateRequestComplete] =
    useState<boolean>(false);

  const navigate = useNavigate();
  const { creditMemos, loading: lookupLoading } = useCreditMemoLookup(tranIds);
  const {
    creationResults,
    loading: creationLoading,
    createCreditMemoSendRequests,
  } = useCreateCreditMemoSendRequests();

  useEffect(() => {
    if (creditMemos.length > 0) {
      setRowData(
        creditMemos.map((cm) => ({
          ...cm,
          status: "",
        }))
      );
    }
  }, [creditMemos]);

  useEffect(() => {
    if (creationResults.length > 0) {
      const updatedRowData = rowData.map((row) => {
        const result = creationResults.find(
          (r: { creditMemoTranId: string }) =>
            r.creditMemoTranId.toUpperCase() ===
            String(row.creditMemoTranId || "").toUpperCase()
        );
        return result
          ? { ...row, status: result.status }
          : { ...row, status: "UNKNOWN" };
      });
      setRowData(updatedRowData);
      setIsCreateRequestComplete(true);
    }
  }, [creationResults]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTranIdsInput(event.target.value);
  };

  const handleLookup = () => {
    const ids = tranIdsInput
      .split(/[ ,]+/)
      .map((id) => id.trim())
      .filter(Boolean);
    setTranIds(ids);
  };

  const handleCreateRequest = throttle(async () => {
    await createCreditMemoSendRequests(tranIds, jiraTicketInput);
  }, 3000);

  const handleReturn = () => {
    navigate("/credit-memos");
  };

  const columns: GridColDef[] = [
    { field: "creditMemoTranId", headerName: "Credit Memo", width: 180 },
    { field: "creditMemoId", headerName: "Internal ID", width: 140 },
    { field: "customerName", headerName: "Customer", flex: 1 },
    { field: "subsidiary", headerName: "Subsidiary", width: 220 },
    {
      field: "total",
      headerName: "Amount",
      type: "number",
      width: 130,
      valueFormatter: (value: number) =>
        (value || 0).toLocaleString("en-US", { minimumFractionDigits: 2 }),
    },
    { field: "found", headerName: "Found", width: 90 },
    { field: "status", headerName: "Status", width: 130 },
  ];

  return (
    <Container maxWidth="xl" sx={{ display: "flex", justifyContent: "center" }}>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mt: 3,
          mb: 3,
          minHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: "90vw",
        }}
      >
        <Typography variant="h5" component="h1" align="center">
          Create France Credit Memo Send
        </Typography>

        <TextField
          label="Credit memo numbers (space or comma separated tranIds)"
          variant="outlined"
          fullWidth
          value={tranIdsInput}
          onChange={handleInputChange}
          onBlur={handleLookup}
        />

        <TextField
          label="Existing Jira ticket (optional)"
          helperText="Leave empty to create a new Finance ticket on error. If set, error details are written to that ticket."
          variant="outlined"
          fullWidth
          value={jiraTicketInput}
          onChange={(event) => setJiraTicketInput(event.target.value)}
        />

        <Box sx={{ mt: 2, height: 400, width: "100%" }}>
          <DataGrid
            rows={rowData}
            columns={columns}
            getRowId={(row) => row.creditMemoTranId}
            disableRowSelectionOnClick
            loading={lookupLoading}
          />
        </Box>

        <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={
              isCreateRequestComplete ? handleReturn : handleCreateRequest
            }
            disabled={lookupLoading || creationLoading || tranIds.length === 0}
          >
            {creationLoading ? (
              <CircularProgress size={24} />
            ) : isCreateRequestComplete ? (
              "Return To Menu"
            ) : (
              "Create Credit Memo Send Request"
            )}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default CreditMemoSendForm;
